import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import './css/UserModal.css';

const UserModal = ({ isOpen, onRequestClose, user }) => {
    const isStandardUser = user?.role === 'user';
    const nationDisplayValue = user?.nation?.number ?? user?.nation ?? 'N/A';
    const [screenshotDataUrl, setScreenshotDataUrl] = useState('');
    const [note, setNote] = useState('');
    const [myRequests, setMyRequests] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingRequests, setIsLoadingRequests] = useState(false);

    const fetchMyRequests = useCallback(async () => {
        if (!isOpen || !user || !isStandardUser) return;
        setIsLoadingRequests(true);
        try {
            const response = await axiosInstance.get(`${API_URL}/users/verification-requests/mine`);
            setMyRequests(response.data || []);
        } catch (error) {
            console.error('Failed to load verification requests:', error);
            setMyRequests([]);
        } finally {
            setIsLoadingRequests(false);
        }
    }, [isOpen, user, isStandardUser]);

    useEffect(() => {
        fetchMyRequests();
    }, [fetchMyRequests]);

    const pendingRequest = myRequests.find((request) => request.status === 'pending');
    const canSubmitVerification = isStandardUser && !pendingRequest && !isSubmitting;

    const handleScreenshotChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            setScreenshotDataUrl('');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setScreenshotDataUrl(String(reader.result || ''));
        reader.readAsDataURL(file);
    };

    const handleSubmitVerification = async () => {
        if (!screenshotDataUrl) {
            window.alert('Please upload a commander profile screenshot.');
            return;
        }

        setIsSubmitting(true);
        try {
            await axiosInstance.post(`${API_URL}/users/verification-requests`, {
                screenshotDataUrl,
                note,
                source: 'profile'
            });
            setScreenshotDataUrl('');
            setNote('');
            await fetchMyRequests();
            window.alert('Verification request submitted.');
        } catch (error) {
            console.error('Failed to submit verification request:', error);
            window.alert(typeof error === 'string' ? error : 'Failed to submit verification request.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen || !user) return null;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel="User Details"
            className="user-modal"
            overlayClassName="user-modal-overlay"
        >
            <h2>User Information</h2>
            <div className="user-info">
                <p><strong>Username:</strong> {user.username}</p>
                <p><strong>Nation:</strong> {nationDisplayValue}</p>
                <p><strong>Role:</strong> {user.role}</p>
            </div>
            {isStandardUser && (
                <div className="user-verify-section">
                    <h3>Request Verification</h3>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        disabled={!canSubmitVerification}
                    />
                    {screenshotDataUrl && (
                        <img className="user-verify-preview" src={screenshotDataUrl} alt="Verification upload preview" />
                    )}
                    <input
                        type="text"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        placeholder="Optional note"
                        disabled={!canSubmitVerification}
                    />
                    <button className="verify-button" onClick={handleSubmitVerification} disabled={!canSubmitVerification}>
                        {pendingRequest ? 'Verification Pending' : isSubmitting ? 'Submitting...' : 'Verify'}
                    </button>
                    <div className="user-verify-status">
                        <strong>My Requests:</strong>{' '}
                        {isLoadingRequests
                            ? 'Loading...'
                            : pendingRequest
                                ? `Pending review (submitted ${new Date(pendingRequest.createdAt).toLocaleString()})`
                                : myRequests.length === 0
                                    ? 'No requests yet'
                                    : `Last request: ${myRequests[0].resolution || myRequests[0].status}`}
                    </div>
                </div>
            )}
            <button className="close-button" onClick={onRequestClose}>Close</button>
        </Modal>
    );
};

export default UserModal;
